/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Collapse } from "@octopusdeploy/design-system-components";
import { ControlledTabsContainer, TabItem } from "@octopusdeploy/design-system-octopus-components";
import cn from "classnames";
import type { Reducer } from "react";
import { useReducer } from "react";
import type { ActionType } from "typesafe-actions";
import { getType, createAction } from "typesafe-actions";
import { CategoryButton } from "~/components/CategoryButton/CategoryButton";
import Section from "~/components/Section";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { combineConstantReducers } from "~/utils/Reducers/combineConstantReducers";
import styles from "./PackagingInstructionSelector.module.less";
import type { PackagingRegistration, PackagingInstruction } from "./Registry";
import { usePackagingInstructions, usePackagingRegistrations } from "./Registry";
import usePackagingRegistration from "./Registry/usePackageRegistration";
//#region state
type RegistrationState = string;
type InstructionState = string;
interface PackagingSelectorState {
    selectedRegistration?: RegistrationState;
    selectedInstruction?: InstructionState;
}
const PackagingSelectorActions = {
    selectRegistration: createAction("SELECT/REGISTRATION", (registration: PackagingRegistration) => registration)(),
    selectInstruction: createAction("SELECT/INSTRUCTION", (instruction: PackagingInstruction) => instruction)(),
};
type PackagingSelectorAction = ActionType<typeof PackagingSelectorActions>;
const autoSelectInstruction = (current: string, instructions: PackagingInstruction[]) => {
    const sortedInstructions = instructions.sort((a, b) => a.displayOrder - b.displayOrder);
    const firstInstruction = sortedInstructions[0] && sortedInstructions[0].name;
    if (!current) {
        return firstInstruction;
    }
    const found = sortedInstructions.filter((x) => current === x.name);
    return found.length > 0 ? found[0].name : firstInstruction;
};
const registrationReducer: Reducer<RegistrationState | undefined, PackagingSelectorAction> = (state = null!, action) => {
    switch (action.type) {
        case getType(PackagingSelectorActions.selectRegistration):
            return action.payload ? action.payload.id : null!;
    }
    return state;
};
const instructionReducer: Reducer<InstructionState | undefined, PackagingSelectorAction> = (state = null!, action) => {
    switch (action.type) {
        case getType(PackagingSelectorActions.selectRegistration):
            return autoSelectInstruction(state, action.payload && action.payload.instructions);
        case getType(PackagingSelectorActions.selectInstruction):
            return action.payload ? action.payload.name : null!;
    }
    return state;
};
const packagingSelectorReducer = combineConstantReducers<PackagingSelectorState, PackagingSelectorAction>({
    selectedRegistration: registrationReducer,
    selectedInstruction: instructionReducer,
});
//#endregion
export const PackagingGroupTitle: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ className, children }) => <div className={cn(styles.groupHeading, className)}>{children}</div>;
PackagingGroupTitle.displayName = "PackagingGroupTitle"
export const PackagingGroupHelp: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ className, children }) => <div className={cn(styles.groupHelp, className)}>{children}</div>;
PackagingGroupHelp.displayName = "PackagingGroupHelp"
const PackagingActions: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ children, className }) => <div className={cn(styles.actions, className)}>{children}</div>;
PackagingActions.displayName = "PackagingActions"
interface PackagingInstructionViewProps {
    registrationId: string;
    instruction: string;
    onSelect: (instruction: PackagingInstruction) => void;
}
const PackagingInstructionView: React.FC<PackagingInstructionViewProps> = ({ registrationId, onSelect, instruction }) => {
    const instructions = usePackagingInstructions(registrationId).sort((a, b) => a.displayOrder - b.displayOrder);
    const registration = usePackagingRegistration(registrationId);
    const handleSelect = (value: string) => {
        const found = instructions.filter((x) => x.name === value);
        if (found.length > 0) {
            onSelect(found[0]);
        }
    };
    return (<Collapse in={!!instruction} timeout="auto" unmountOnExit={true}>
            {instruction && (<div className={styles.contentContainer}>
                    <div className={styles.instructionsIntro}>
                        How would you like to package this <b>{registration.name}</b> application?
                    </div>
                    <Section>
                        <ControlledTabsContainer value={instruction} onChange={handleSelect}>
                            {instructions.map((x) => (<TabItem label={x.name} value={x.name} key={x.name}>
                                    <TransitionAnimation key={`${registrationId}-${instruction}`}>{x.render()}</TransitionAnimation>
                                </TabItem>))}
                        </ControlledTabsContainer>
                    </Section>
                </div>)}
        </Collapse>);
};
PackagingInstructionView.displayName = "PackagingInstructionView"
type PackagingInstructionSelectorProps = {};
export const PackagingInstructionSelector: React.FC<PackagingInstructionSelectorProps> = (props) => {
    const registrations = usePackagingRegistrations();
    const [state, dispatch] = useReducer(packagingSelectorReducer, { selectedInstruction: null!, selectedRegistration: null! });
    return (<>
            <div>
                What type of <b>software application</b> do you want to package?
            </div>
            <Section>
                <PackagingActions className={styles.paperActions}>
                    {registrations.map((x) => (<CategoryButton label={x.name} onClick={() => dispatch(PackagingSelectorActions.selectRegistration(x))} key={x.id} isActive={state.selectedRegistration === x.id}/>))}
                </PackagingActions>
            </Section>
            <PackagingInstructionView onSelect={(instruction) => dispatch(PackagingSelectorActions.selectInstruction(instruction))} instruction={state.selectedInstruction!} registrationId={state.selectedRegistration!}/>
        </>);
};
PackagingInstructionSelector.displayName = "PackagingInstructionSelector"
